import React, { useContext, useState } from 'react';
import ProcessHandlingListContext from '../../../components/ProcessHandlingListContext';
import useBeep from '@afosto/utils-react/useBeep';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useScanner from '@afosto/utils-react/input/useScanner';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Box from '@material-ui/core/Box';
import Hidden from '@material-ui/core/Hidden';
import FinishButton from '../../../components/FinishButton';
import ItemResultsDialog from '../../../components/ItemResultsDialog';
import ItemQuantityDialog from '../../../components/ItemQuantityDialog';
import MobileActionBar from '../../../components/MobileActionBar';
import PositionDialog from '../../../components/PositionDialog';
import ProductNotFoundDialog from '../../../components/ProductNotFoundDialog';
import PutawayHistoryDialog from './PutawayHistoryDialog';
import PutawayList from './PutawayList';
import PutawayMobileContainer from './PutawayMobileContainer';
import AddProductDrawer from '@afosto/app-components-react/AddProductDrawer';
import BulkButton from '@afosto/components-react/BulkButton';
import ProgressDialog from '@afosto/app-components-react/ProgressDialog';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { bulk as bulkApi } from '@afosto/fulfilment/api/Bulk';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { items as itemsApi } from '@afosto/fulfilment/api/Items';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';

const DEFAULT_ITEM_STATUS_CONSTRAINTS = [
  {
    key: 'is_handled',
    operator: 'in',
    value: false,
  },
  {
    key: 'is_finished',
    operator: 'in',
    value: false,
  },
  {
    key: 'is_missing',
    operator: 'in',
    value: false,
  },
];

const Putaway = () => {
  const confirm = useConfirm();
  const history = useHistory();
  const intl = useIntl();
  const playBeep = useBeep();
  const { enqueueSnackbar } = useSnackbar();
  const {
    handlingList,
    setShowAddItemDialog,
    setShowHistory,
    showAddItemDialog,
    showHistory,
  } = useContext(ProcessHandlingListContext);
  const [activeItem, setActiveItem] = useState(null);
  const [isPuttingAwayProducts, setIsPuttingAwayProducts] = useState(false);
  const [itemResults, setItemResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showItemResultsDialog, setShowItemsResultsDialog] = useState(false);
  const [showPositionDialog, setShowPositionDialog] = useState(false);
  const [showProductNotFoundDialog, setShowProductNotFoundDialog] = useState(false);
  const [preferredPositions, setPreferredPositions] = useState({});
  const handlingListId = handlingList?.id;

  const getPreferredPositionsForSkus = async skus => {
    try {
      const positionsResponse = await fulfilmentApi
        .searchPositions(handlingList.location_id, skus)
        .catch(() => {});
      const { data: positions = [] } = positionsResponse ?? {};
      const formattedPositions = positions.reduce(
        (acc, positionData) => ({ ...acc, [positionData.sku]: positionData.position }),
        {},
      );

      const updatedPositions = { ...preferredPositions, ...formattedPositions };
      setPreferredPositions(updatedPositions);

      return updatedPositions;
    } catch (err) {
      return {};
    }
  };

  const fetchItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        searchApi.searchHandlingListItems(
          {
            constraints: [
              {
                key: 'list_ids',
                operator: 'in',
                value: [handlingListId],
              },
              ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
            ],
          },
          requestOptions,
        ),
      {
        transformData: async (data, source) => {
          const { pagination = {} } = source ?? {};
          const { itemsPerPage } = pagination;
          const skus = data.map(({ sku }) => sku);
          const products = await fetchMultipleProducts(
            skus,
            {},
            {
              headers: {
                'x-page-size': itemsPerPage,
              },
            },
          );
          const mappedProducts = products.reduce(
            (acc, product) => ({ ...acc, [product?.sku]: product }),
            {},
          );

          const skusWithoutPosition = [
            ...new Set(
              data
                .filter(item => !item.position && !preferredPositions[item.sku])
                .map(item => item.sku),
            ),
          ];

          let allPreferredPositions = preferredPositions;

          if (skusWithoutPosition?.length > 0) {
            allPreferredPositions = await getPreferredPositionsForSkus(skusWithoutPosition);
          }

          return data.reduce((acc, item) => {
            const [firstId] = item?.ids ?? [];
            const position = item?.position || allPreferredPositions[item.sku];

            return [...acc, { ...item, id: firstId, position, product: mappedProducts[item.sku] }];
          }, []);
        },
      },
    );

  const dataSource = useDataSource(fetchItems, {
    modules: ['pagination', 'selection'],
    pagination: {
      itemsPerPage: 10,
    },
    selection: {
      onAddSelection: selection => {
        setSelectedItems(oldItems => {
          const currentItemIds = oldItems.map(item => item.id);
          const newItems = selection.filter(item => !currentItemIds.includes(item.id));

          return [...oldItems, ...newItems];
        });
      },
      onRemoveSelection: selection => {
        const removedIds = selection.map(item => item.id);
        setSelectedItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
      },
    },
  });

  const handleUnknownItem = async product => {
    const confirmResponse = await confirm({
      title: intl.formatMessage(translations.unknownItemDialog.title),
      description: intl.formatMessage(translations.unknownItemDialog.description),
      catchOnCancel: false,
      buttons: [
        {
          label: intl.formatMessage(translations.unknownItemDialog.buttons.cancel),
          type: 'cancel',
          variant: 'text',
          color: 'primary',
        },
        {
          label: intl.formatMessage(translations.unknownItemDialog.buttons.add),
          type: 'submit',
          response: 'add',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

    if (confirmResponse !== 'add') {
      return;
    }

    const shipments = handlingList?.shipments ?? [];

    if (shipments?.length === 1) {
      const [firstShipmentId] = shipments;
      const { ean, sku, name } = product ?? {};
      let position = preferredPositions[sku];

      if (!position) {
        const positions = await getPreferredPositionsForSkus([sku]);
        position = positions[sku];
      }

      setActiveItem({
        description: name,
        ean,
        quantity: 0,
        sku,
        product,
        position,
        shipment_id: firstShipmentId,
        isNew: true,
      });
    }
  };

  const handleSelectItemResult = selectedItem => {
    setActiveItem(selectedItem);
    setItemResults([]);
    setShowItemsResultsDialog(false);
  };

  const handleScanInput = async value => {
    try {
      const products = await fetchMultipleProducts([value]);
      const filteredProducts = products.filter(product => product.ean === value);

      if (filteredProducts.length) {
        const skus = filteredProducts.map(product => product.sku);
        const response = await searchApi.searchHandlingListItems({
          constraints: [
            {
              key: 'list_ids',
              operator: 'in',
              value: [handlingListId],
            },
            {
              key: 'skus',
              operator: 'in',
              value: skus,
            },
            ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
          ],
        });
        const { data: items = [] } = response ?? {};

        if (items?.length >= 1 && filteredProducts?.length > 1) {
          const formattedResults = items.reduce((acc, item) => {
            const product = findObjectInArray(filteredProducts, 'sku', item?.sku);
            return [...acc, { ...item, product }];
          }, []);
          setItemResults(formattedResults);
          setShowItemsResultsDialog(true);
          return;
        }

        if (items?.length > 0) {
          const [firstItem] = items;
          const product = findObjectInArray(filteredProducts, 'sku', firstItem?.sku);
          setActiveItem({ ...firstItem, product });
          return;
        }

        if (!items?.length && filteredProducts?.length === 1) {
          const [product] = filteredProducts;

          if (product) {
            handleUnknownItem(product).catch(() => {});
            return;
          }
        }
      }

      if (value) {
        playBeep('error');
        setShowProductNotFoundDialog(true);
      }
    } catch (error) {
      if (value) {
        playBeep('error');
      }
    }
  };

  useScanner(handleScanInput, { disabled: !!activeItem });

  const handleClickItem = (event, item) => {
    setActiveItem(item);
  };

  const handleCloseItemDialog = () => {
    setActiveItem(null);
  };

  const handleCloseHistoryDialog = () => {
    setShowHistory(false);
  };

  const handleCloseAddItemDialog = () => {
    setShowAddItemDialog(false);
  };

  const handleCloseItemResultsDialog = () => {
    setShowItemsResultsDialog(false);
  };

  const handleClosePositionDialog = () => {
    setShowPositionDialog(false);
  };

  const handleCloseProductNotFoundDialog = () => {
    setShowProductNotFoundDialog(false);
  };

  const handleAddItem = async ({ sku, description, ean, product }) => {
    try {
      const shipments = handlingList?.shipments ?? [];

      if (shipments?.length === 1) {
        const [firstShipmentId] = shipments;
        let position = preferredPositions[sku];

        if (!position) {
          const positions = await getPreferredPositionsForSkus([sku]);
          position = positions[sku];
        }

        setActiveItem({
          description,
          ean,
          quantity: 0,
          sku,
          product,
          position,
          shipment_id: firstShipmentId,
          isNew: true,
        });
      }

      setShowAddItemDialog(false);
    } catch (err) {
      // Do nothing.
    }
  };

  const updateItems = async (ids, position) =>
    bulkApi.bulkUpdateItems({
      command: 'set_parameters',
      parameters: {
        is_handled: true,
        is_finished: true,
        position,
      },
      constraints: [
        {
          key: 'list_ids',
          operator: 'in',
          value: [handlingListId],
        },
        ...(ids?.length > 0
          ? [
              {
                key: 'ids',
                operator: 'in',
                value: ids,
              },
            ]
          : []),
        ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
      ],
    });

  const handleConfirmOverage = async () =>
    confirm({
      title: intl.formatMessage(translations.confirmOverageDialog.title),
      description: intl.formatMessage(translations.confirmOverageDialog.description),
      catchOnCancel: false,
      disableBackdropClick: true,
      buttons: [
        {
          label: intl.formatMessage(translations.confirmOverageDialog.buttons.cancel),
          type: 'cancel',
          variant: 'text',
          color: 'primary',
        },
        {
          label: intl.formatMessage(translations.confirmOverageDialog.buttons.save),
          type: 'submit',
          response: 'save',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

  const handleSaveItem = async ({ requested, quantity, position }, item) => {
    try {
      if (requested > 0 && quantity > requested) {
        const confirmResponse = await handleConfirmOverage();

        if (confirmResponse !== 'save') {
          return;
        }
      }

      let ids = [];

      if (quantity < requested) {
        ids = (item?.ids ?? []).slice(0, quantity);
      } else if (quantity >= requested) {
        ids = [...(item?.ids ?? [])];
      }

      if (ids.length) {
        await updateItems(ids, position);
      }

      if (quantity > requested) {
        const overageQuantity = quantity - requested;
        const response = await shipmentsApi.viewShipment(item?.shipment_id);
        const { data: shipment } = response ?? {};

        await itemsApi.updateShipmentOverage(shipment?.id, [
          ...(shipment?.overage ?? []),
          {
            sku: item.sku,
            description: item.description,
            quantity: overageQuantity,
            position,
          },
        ]);
      }

      const positionsResponse = await fulfilmentApi
        .searchPositions(handlingList.location_id, [item.sku])
        .catch(() => {});

      if (positionsResponse?.data && positionsResponse?.data?.length === 0) {
        await fulfilmentApi
          .upsertLocationSkus(handlingList.location_id, [
            {
              sku: item.sku,
              position,
            },
          ])
          .catch(() => {});
      }

      dataSource.reloadDataSource();
      setActiveItem(null);
    } catch (error) {
      playBeep('error');
      enqueueSnackbar(intl.formatMessage(translations.feedback.itemsNotPutaway), {
        variant: 'error',
      });
    }
  };

  const handlePutAwayMultipleItems = async ({ position }) => {
    try {
      setIsPuttingAwayProducts(true);
      setShowPositionDialog(false);

      const allItemsSelected = dataSource.selection.allItemsSelected ?? false;
      const itemIds = selectedItems.reduce((acc, item) => [...acc, ...(item?.ids ?? [])], []);

      if (!allItemsSelected && itemIds.length === 0) {
        return;
      }

      await updateItems(allItemsSelected ? [] : itemIds, position);

      enqueueSnackbar(intl.formatMessage(translations.feedback.multipleItemsPutaway), {
        variant: 'success',
      });

      setSelectedItems([]);
      dataSource.selection.setAllItemsSelected(false);
      dataSource.selection.setSelectedItems([]);
      dataSource.pagination.setActivePage(0);
      dataSource.pagination.setPageCursor(null);
      dataSource.reloadDataSource();
    } catch (error) {
      playBeep('error');
      enqueueSnackbar(intl.formatMessage(translations.feedback.multipleItemsNotPutaway), {
        variant: 'error',
      });
    } finally {
      setIsPuttingAwayProducts(false);
    }
  };

  const reportMissingItems = async () =>
    bulkApi.bulkUpdateItems({
      command: 'set_parameters',
      parameters: {
        is_missing: true,
      },
      constraints: [
        {
          key: 'list_ids',
          operator: 'in',
          value: [handlingListId],
        },
        ...DEFAULT_ITEM_STATUS_CONSTRAINTS,
      ],
    });

  const handleFinish = async () => {
    try {
      if (dataSource?.data?.length > 0) {
        const response = await confirm({
          title: intl.formatMessage(translations.remainingItemsDialog.title),
          description: intl.formatMessage(translations.remainingItemsDialog.description),
          disableBackdropClick: true,
          catchOnCancel: false,
          buttons: [
            {
              label: intl.formatMessage(translations.remainingItemsDialog.buttons.cancel),
              type: 'cancel',
              variant: 'outlined',
              style: {
                marginRight: 'auto',
              },
            },
            {
              label: intl.formatMessage(translations.remainingItemsDialog.buttons.forceFinish),
              type: 'submit',
              response: 'force-finish',
              variant: 'contained',
              color: 'primary',
            },
          ],
        });

        if (response === 'cancel') {
          return;
        }

        await reportMissingItems();
      }

      history.push('/shipments');
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.putawayNotFinished), {
        variant: 'error',
      });
    }
  };

  const bulkActions = [
    {
      key: 'putawayMultiple',
      render: () => (
        <BulkButton variant="outlined" size="small" onClick={() => setShowPositionDialog(true)}>
          {intl.formatMessage(translations.bulkActions.putaway)}
        </BulkButton>
      ),
    },
  ];

  return (
    <>
      <Hidden mdUp implementation="js">
        <PutawayMobileContainer>
          <Box p={2}>
            <PutawayList
              bulkActions={bulkActions}
              dataSource={dataSource}
              onClick={handleClickItem}
            />
          </Box>
          <MobileActionBar>
            <FinishButton onClick={handleFinish} style={{ marginLeft: 'auto' }} />
          </MobileActionBar>
        </PutawayMobileContainer>
      </Hidden>
      <Hidden smDown implementation="js">
        <PutawayList bulkActions={bulkActions} dataSource={dataSource} onClick={handleClickItem} />
        <Box pt={3} textAlign="right">
          <FinishButton onClick={handleFinish} />
        </Box>
      </Hidden>
      <ItemResultsDialog
        isOpen={showItemResultsDialog}
        onClose={handleCloseItemResultsDialog}
        onSelect={handleSelectItemResult}
        results={itemResults}
      />
      <ItemQuantityDialog
        title={intl.formatMessage(translations.itemQuantityDialog.title)}
        isOpen={!!activeItem}
        item={activeItem}
        locationId={handlingList.location_id}
        onClose={handleCloseItemDialog}
        onSave={handleSaveItem}
        quantityCounterProps={{
          requestedLabel: intl.formatMessage(
            translations.itemQuantityDialog.quantityCounter.received,
          ),
          quantityLabel: intl.formatMessage(
            translations.itemQuantityDialog.quantityCounter.putaway,
          ),
        }}
        allowMoreThanRequested={activeItem?.isNew === true}
        saveOnQuantityReached={!activeItem?.isNew}
      />
      <PositionDialog
        isOpen={showPositionDialog}
        onClose={handleClosePositionDialog}
        onSubmit={handlePutAwayMultipleItems}
        locationId={handlingList.location_id}
        title={intl.formatMessage(translations.positionDialog.title)}
      />
      <ProductNotFoundDialog
        isOpen={showProductNotFoundDialog}
        onClose={handleCloseProductNotFoundDialog}
      />
      <ProgressDialog
        isOpen={isPuttingAwayProducts}
        message={intl.formatMessage(translations.progressDialog.isPuttingAway)}
      />
      <PutawayHistoryDialog isOpen={showHistory} onClose={handleCloseHistoryDialog} />
      <AddProductDrawer
        formProps={{
          addAnotherProduct: false,
          allowNegativeQuantity: false,
          showAddAnotherProduct: false,
          showQuantity: false,
        }}
        isOpen={showAddItemDialog}
        onAdd={handleAddItem}
        onClose={handleCloseAddItemDialog}
      />
    </>
  );
};

export default Putaway;
